import {
	FunctionComponent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { FieldProps } from "@animech/axiom-ui-builder";
import * as Styled from "./thumbnail-dropdown.style";
import { Root } from "@definitions";
import { useUiData } from "src/ui/data";

export type ExtendedThumbnailDropDownProps = {
	showLabel?: boolean;
	hiddenValues?: unknown[];
};

export type ThumbnailDropDownProps = FieldProps<Root> &
	ExtendedThumbnailDropDownProps;

export const ThumbnailDropDown: FunctionComponent<ThumbnailDropDownProps> = ({
	fieldValues = [],
	value,
	onValueChange,
	fieldMeta,
	fieldValuesMeta,
	showLabel = false,
	hiddenValues,
}) => {
	const { localizer } = useUiData();
	const [isOpen, setIsOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const optionListRef = useRef<HTMLDivElement>(null);
	const isHiddenValue =
		hiddenValues != null &&
		hiddenValues.length > 0 &&
		hiddenValues.includes(value);

	const { selectableValues, selectableMeta } = useMemo(() => {
		if (!hiddenValues || hiddenValues.length === 0) {
			return {
				selectableValues: fieldValues,
				selectableMeta: fieldValuesMeta,
			};
		}
		const indices: number[] = [];
		const values = fieldValues.filter((v, i) => {
			const keep = !hiddenValues.includes(v);
			if (keep) {
				indices.push(i);
			}
			return keep;
		});
		return {
			selectableValues: values,
			selectableMeta: fieldValuesMeta
				? indices.map((i) => fieldValuesMeta[i])
				: undefined,
		};
	}, [fieldValues, fieldValuesMeta, hiddenValues]);

	const displayValue = value;

	const displayIndex = fieldValues.indexOf(displayValue);
	const displayMeta = fieldValuesMeta?.[displayIndex];
	const displayImage = displayMeta?.image;
	const displayLabel = displayMeta?.label ?? String(displayValue ?? "");

	const close = useCallback(() => setIsOpen(false), []);

	// Close on outside click
	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const handleMouseDown = (e: MouseEvent) => {
			if (
				triggerRef.current?.contains(e.target as Node) ||
				optionListRef.current?.contains(e.target as Node)
			) {
				return;
			}
			close();
		};
		document.addEventListener("mousedown", handleMouseDown);
		return () => document.removeEventListener("mousedown", handleMouseDown);
	}, [isOpen, close]);

	// Close on Escape
	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				close();
			}
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [isOpen, close]);

	// Block background scroll while open (allow scroll inside option list)
	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const blockScroll = (e: Event) => {
			if (optionListRef.current?.contains(e.target as Node)) {
				return;
			}
			e.preventDefault();
		};
		document.addEventListener("wheel", blockScroll, { passive: false });
		document.addEventListener("touchmove", blockScroll, { passive: false });
		return () => {
			document.removeEventListener("wheel", blockScroll);
			document.removeEventListener("touchmove", blockScroll);
		};
	}, [isOpen]);

	// Position the option list below or above the trigger (flips when near viewport bottom)
	const getOptionListStyle = (): React.CSSProperties => {
		if (!triggerRef.current) {
			return {};
		}
		const rect = triggerRef.current.getBoundingClientRect();
		const maxHeight = 300;
		const gap = 2;
		const spaceBelow = window.innerHeight - rect.bottom;
		const opensUpward = spaceBelow < maxHeight && rect.top > spaceBelow;

		return {
			left: rect.left,
			width: rect.width,
			...(opensUpward
				? { bottom: window.innerHeight - rect.top + gap }
				: { top: rect.bottom + gap }),
		};
	};

	const handleSelect = (fieldValue: unknown) => {
		onValueChange?.(fieldValue);
		close();
	};

	// Static display when: no selectable options at all, OR only one option and it's the current value
	if (
		selectableValues.length === 0 ||
		(selectableValues.length <= 1 && !isHiddenValue)
	) {
		return (
			<Styled.Root>
				{showLabel && fieldMeta?.label && (
					<Styled.FieldLabel>{fieldMeta.label}</Styled.FieldLabel>
				)}
				<Styled.Trigger as="div" style={{ cursor: "default" }}>
					{displayImage && (
						<Styled.TriggerThumbnail
							src={displayImage}
							alt=""
							draggable={false}
						/>
					)}
					<Styled.TriggerLabel>{displayLabel}</Styled.TriggerLabel>
				</Styled.Trigger>
			</Styled.Root>
		);
	}

	return (
		<Styled.Root>
			{showLabel && fieldMeta?.label && (
				<Styled.FieldLabel>{fieldMeta.label}</Styled.FieldLabel>
			)}
			<Styled.Trigger
				ref={triggerRef}
				onClick={() => setIsOpen((prev) => !prev)}
				type="button"
				data-oos={isHiddenValue ? "" : undefined}
			>
				{displayImage && (
					<Styled.TriggerThumbnail
						src={displayImage}
						alt=""
						draggable={false}
					/>
				)}
				<Styled.TriggerLabel>{displayLabel}</Styled.TriggerLabel>
				{isHiddenValue && (
					<Styled.OosLabel>
						{localizer.localizeTerm("itemNotInStock")}
					</Styled.OosLabel>
				)}
				<Styled.ArrowIcon>{isOpen ? "\u25B2" : "\u25BC"}</Styled.ArrowIcon>
			</Styled.Trigger>

			{isOpen &&
				createPortal(
					<Styled.OptionList ref={optionListRef} style={getOptionListStyle()}>
						{selectableValues.map((fieldValue, index) => {
							const meta = selectableMeta?.[index];
							const image = meta?.image;
							const label = meta?.label ?? String(fieldValue ?? "");
							const isSelected = fieldValue === value;

							return (
								<Styled.Option
									key={index}
									data-selected={isSelected}
									onClick={() => handleSelect(fieldValue)}
								>
									{image && (
										<Styled.OptionThumbnail
											src={image}
											alt=""
											draggable={false}
										/>
									)}
									<Styled.OptionLabel>{label}</Styled.OptionLabel>
									<Styled.SelectedIcon data-visible={isSelected}>
										&#10003;
									</Styled.SelectedIcon>
								</Styled.Option>
							);
						})}
					</Styled.OptionList>,
					document.body,
				)}
		</Styled.Root>
	);
};
